import React, { useEffect } from 'react'
import { createPortal } from 'react-dom'
import styled from 'styled-components'

const ModalOverlay = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: ${({ isOpen }) => (isOpen ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: fadeIn 0.3s ease-out;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  ${({ theme }) => theme.mediaWidth.upToSmall`
    align-items: flex-end;
  `}
`

const ModalContent = styled.div`
  overflow: hidden;
  background: ${({ theme }) => theme.bg0};
  border-radius: 20px;
  width: 100%;
  max-width: 420px;
  position: relative;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  animation: fadeUp 0.3s ease-out;

  @keyframes fadeUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  ${({ theme }) => theme.mediaWidth.upToSmall`
    max-width: 100%;
    border-radius: 20px 20px 0 0;
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    animation: slideUp 0.3s ease-out;

    @keyframes slideUp {
      from {
        transform: translateY(100%);
      }
      to {
        transform: translateY(0);
      }
    }
  `}
`

interface ModalProps {
  isOpen: boolean
  onDismiss: () => void
  children: React.ReactNode
  maxHeight?: number
  minHeight?: number | false
  style?: React.CSSProperties
}

export default function Modal({ isOpen, onDismiss, children, maxHeight, minHeight, style }: ModalProps) {
  const [modalRoot, setModalRoot] = React.useState<HTMLElement | null>(null)

  useEffect(() => {
    let root = document.getElementById('modal-root')
    if (!root) {
      root = document.createElement('div')
      root.setAttribute('id', 'modal-root')
      document.body.appendChild(root)
    }
    setModalRoot(root)

    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isOpen])

  if (!modalRoot) return null

  return createPortal(
    <ModalOverlay isOpen={isOpen} onClick={onDismiss}>
      <ModalContent onClick={(e) => e.stopPropagation()} style={style}>
        {children}
      </ModalContent>
    </ModalOverlay>,
    modalRoot
  )
}
